/**
 * My flat — big flat-code card with BHK/ownership rows + co-resident list.
 * Wired: GET /api/account/my-flat (falls back to the session user's flat if
 * the endpoint isn't live yet). Port of screens-account.jsx MyFlat.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, str } from '@/components/gate/api';
import { goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Avatar, Card, EmptyState, Hairline, Screen, SectionLabel, Skeleton, TopBar } from '@/ui';

type Resident = { id: string; name: string; rel: string; me: boolean };
type Flat = {
  code: string; bhk: string; tower?: string; floor?: string;
  facts: Array<[labelKey: DictKey, value: string]>;
  residents: Resident[];
};

function parseFlat(body: unknown): Flat | null {
  const src = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const f = (src.flat && typeof src.flat === 'object' ? src.flat : src) as Record<string, unknown>;
  const code = str(f, 'code', 'flatCode');
  if (!code) return null;

  const facts: Array<[DictKey, string]> = [];
  const ownership = str(f, 'ownership', 'ownershipLabel');
  const carpet = str(f, 'carpetArea', 'area');
  const parking = str(f, 'parking', 'parkingSlots');
  if (ownership) facts.push(['mf_ownership', ownership]);
  if (carpet) facts.push(['mf_carpet', carpet]);
  if (parking) facts.push(['mf_parking', parking]);

  const residentsRaw = Array.isArray(src.residents) ? src.residents
    : Array.isArray(f.residents) ? f.residents : [];
  const residents: Resident[] = residentsRaw.map((r, i) => ({
    id: str(r, 'id') ?? `r${i}`,
    name: str(r, 'name', 'fullName') ?? 'Resident',
    rel: str(r, 'rel', 'relation', 'role', 'residentType') ?? '',
    me: bool(r, 'me', 'isMe') ?? false,
  }));

  return {
    code,
    bhk: str(f, 'bhk') ?? '',
    tower: str(f, 'tower', 'wing'),
    floor: str(f, 'floor', 'floorLabel'),
    facts,
    residents,
  };
}

export default function MyFlatScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { user } = useSession();
  const [flat, setFlat] = React.useState<Flat | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get<unknown>('/api/account/my-flat');
        if (cancelled) return;
        const parsed = parseFlat(body);
        if (parsed) {
          setFlat(parsed);
          setState('ready');
          return;
        }
        throw new Error('empty');
      } catch {
        if (cancelled) return;
        // Endpoint not live yet — fall back to the flat on the session.
        if (user?.flat) {
          setFlat({
            code: user.flat.code, bhk: user.flat.bhk, facts: [],
            residents: [{ id: 'me', name: user.fullName, rel: user.residentType, me: true }],
          });
          setState('ready');
        } else {
          setState('error');
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Screen scroll={false}>
      <TopBar title={t('mf_title')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            <Skeleton h={160} />
            {[0, 1].map((i) => <Skeleton key={i} h={64} />)}
          </View>
        ) : state === 'error' || !flat ? (
          <EmptyState
            icon={<Icon name="home" size={40} color={colors.textTertiary} />}
            title={t('mf_err_title')}
            body={t('mf_err_body')}
          />
        ) : (
          <>
            <Card>
              <AppText variant="displayLg">{flat.code}</AppText>
              <AppText variant="bodyMd" tone="tertiary" style={{ marginTop: space.s1 }}>
                {[flat.tower, flat.floor, flat.bhk].filter(Boolean).join(' · ') || t('mf_registered')}
              </AppText>
              {flat.facts.length > 0 ? (
                <>
                  <View style={{ marginTop: space.s3 }}><Hairline /></View>
                  <View style={{ marginTop: space.s3, gap: space.s2 }}>
                    {flat.facts.map(([labelKey, value]) => (
                      <View key={labelKey} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <AppText variant="bodySm" tone="tertiary">{t(labelKey)}</AppText>
                        <AppText variant="bodyMd" weight="500">{value}</AppText>
                      </View>
                    ))}
                  </View>
                </>
              ) : null}
            </Card>
            <SectionLabel style={{ marginTop: space.s6 }}>{t('mf_residents')}</SectionLabel>
            <View style={{ gap: space.s2 }}>
              {flat.residents.map((r) => (
                <Card key={r.id}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                    <Avatar name={r.name} size={40} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight="500">
                        {r.name}{r.me ? ` (${t('mf_you')})` : ''}
                      </AppText>
                      {r.rel ? <AppText variant="bodySm" tone="tertiary">{r.rel}</AppText> : null}
                    </View>
                  </View>
                </Card>
              ))}
            </View>
          </>
        )}
      </Screen>
    </Screen>
  );
}
